import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { router } from '@inertiajs/react';
import { buildTextOffsetMap, expandToWordBoundaries } from '../../lib/textOffsetMap';
import { CHAPTER_CONTENT_CLASSES } from '../../lib/chapterContentClasses';
import { cn } from '../../lib/cn';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import SelectionToolbar from './SelectionToolbar';
import SelectionRenderer from './SelectionRenderer';
import HighlightHoverMenu from './HighlightHoverMenu';

/**
 * Reading surface for a BookChapter that supports the full vertical-nav
 * selection vocabulary: highlight, comment, close reading, analyze sentence,
 * look up word.
 *
 * Props:
 *   book:      { id, title, author }
 *   chapter:   { id, title, formatted_label }
 *   html:      full chapter HTML string (from /teacher/library/.../chapters/:id/html)
 *   initialSelections: array of TextSelection JSON
 *
 * The component owns the selection list after mount, creating/updating/deleting
 * via the nested text_selections endpoints.
 */
export default function ChapterReader({
  book,
  chapter,
  html,
  initialSelections = [],
  analyzedSentences = [],
  onSelectionsChange,
  focusedChunkRange = null,      // { start, end } chapter-block indices
  focusedParagraphIndex = null,  // chapter-block index
  courseListOptions = null,      // [{ id, name, course_id, course_title }] — enables teacher add-to-course-list
  readOnly = false,              // suppress the selection toolbar (e.g. a student following a live read)
}) {
  const contentRef = useRef(null);
  // Keep offsetMap in a ref so handlers always see the latest without
  // depending on a state-driven rebind. We also mirror it to state so
  // SelectionRenderer (which is a child) re-runs on changes.
  const offsetMapRef = useRef(null);
  const [offsetMap, setOffsetMap] = useState(null);
  const [selections, setSelections] = useState(initialSelections);
  // Keep the latest selections in a ref so handlers can read & mutate them
  // synchronously without racing with React's state commit order. This is
  // critical: previously we called onSelectionsChange inside a setSelections
  // updater, which React StrictMode double-invokes — resulting in the parent
  // receiving a stale / duplicated array and the new highlight failing to
  // persist into the reader's render.
  const selectionsRef = useRef(initialSelections);
  useEffect(() => { selectionsRef.current = selections; }, [selections]);
  const [pending, setPending] = useState(null); // { rect, text, html, start, end, wordCount, expandedWord }
  const [hoveredHighlight, setHoveredHighlight] = useState(null); // { selection, rect }
  const [listToast, setListToast] = useState(null); // { kind: 'success'|'error', word, label }
  const hoverTimeoutRef = useRef(null);
  const { push: pushCrumb } = useBreadcrumbs();

  // Push book + chapter crumbs on mount and whenever chapter changes.
  useEffect(() => {
    if (!book) return;
    pushCrumb({
      level: 'book',
      label: book.title,
      href: `/teacher/library/${book.id}`,
    });
    if (chapter) {
      pushCrumb({
        level: 'chapter',
        label: chapter.formatted_label || chapter.title,
        href: `/teacher/library/${book.id}?chapter=${chapter.id}`,
      });
    }
  }, [book?.id, chapter?.id]);

  // Rebuild the offset map whenever the HTML is (re)rendered. useLayoutEffect
  // ensures this happens before the browser paints — so the very first
  // mouseup after the chapter loads can find a populated map.
  useLayoutEffect(() => {
    if (!contentRef.current) return;
    const map = buildTextOffsetMap(contentRef.current);
    offsetMapRef.current = map;
    setOffsetMap(map);
  }, [html]);

  // Apply highlight classes to chunk-range and focused paragraph. The chapter
  // HTML arrives pre-tagged with data-paragraph-index by the backend
  // (ChapterHtmlParagraphIndexer) so we can target paragraphs by their stored
  // chunk paragraph_start/paragraph_end without re-walking block-element rules.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const paras = root.querySelectorAll('[data-paragraph-index]');
    const SOFT = ['bg-yellow-50/40', 'dark:bg-yellow-900/10'];
    const STRONG = ['bg-yellow-50', 'dark:bg-yellow-900/20', 'border-l-4', 'border-yellow-400', '-mx-4', 'px-4', 'rounded-lg'];
    const ALL = [...SOFT, ...STRONG, 'transition-colors'];

    paras.forEach((p) => p.classList.remove(...ALL));

    // Soft band for a search-result chunk range (optional).
    if (focusedChunkRange) {
      paras.forEach((p) => {
        const idx = Number(p.dataset.paragraphIndex);
        if (idx < focusedChunkRange.start || idx > focusedChunkRange.end) return;
        p.classList.add(...SOFT, 'transition-colors');
      });
    }

    // Strong highlight for the single focused paragraph — a search hit OR a
    // live-session pin. Applies independently of any chunk range.
    if (focusedParagraphIndex != null) {
      const focused = root.querySelector(`[data-paragraph-index="${focusedParagraphIndex}"]`);
      if (focused) {
        focused.classList.remove(...SOFT);
        focused.classList.add(...STRONG, 'transition-colors');
      }
    }
  }, [html, focusedChunkRange, focusedParagraphIndex]);

  // Sync selections from the parent whenever they arrive. The parent fetches
  // text_selections async, so initialSelections starts as [] and fills in
  // after the network round-trip — this effect picks up the late update.
  // We also keep selectionsRef in lockstep so commitSelections reads from a
  // fresh baseline when the user next mutates.
  useEffect(() => {
    const next = initialSelections || [];
    selectionsRef.current = next;
    setSelections(next);
  }, [initialSelections]);

  const handleSelectionMouseUp = useCallback(() => {
    // Let the browser finish committing the selection before we read it.
    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return; // don't clobber existing pending on a stray click

      const rangeText = sel.toString();
      if (!rangeText.trim()) return;

      const container = contentRef.current;
      if (!container) return;

      const range = sel.getRangeAt(0);

      // Anchor the selection only if both endpoints are inside our container.
      if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
        console.warn('[ChapterReader] selection bounds outside chapter content; ignoring', {
          startInContainer: container.contains(range.startContainer),
          endInContainer: container.contains(range.endContainer),
        });
        return;
      }

      // Build the offset map fresh against the live DOM. Pre-building is
      // unreliable: text nodes can be merged by mark unwrapping (.normalize)
      // or browser reflows, which invalidates any cached node references.
      const map = buildTextOffsetMap(container);
      offsetMapRef.current = map;
      setOffsetMap(map);

      const offsets = map.rangeToOffsets(range);
      if (!offsets) {
        console.warn('[ChapterReader] could not resolve selection to offsets', {
          plainTextLen: map.plainText.length,
          nodeCount: map.nodes.length,
          startNode: range.startContainer,
        });
        return;
      }

      const expanded = expandToWordBoundaries(map.plainText, offsets.start, offsets.end);
      const rawTrimmed = rangeText.trim();
      const wordCount = rawTrimmed.split(/\s+/).length;

      const frag = range.cloneContents();
      const wrapper = document.createElement('div');
      wrapper.appendChild(frag);

      const rect = range.getBoundingClientRect();

      setPending({
        rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        text: rawTrimmed,
        html: wrapper.innerHTML,
        start: offsets.start,
        end: offsets.end,
        wordCount,
        expandedWord: wordCount === 1 ? expanded.word : null,
      });
    }, 50);
  }, []);

  // Bind mouseup directly to the content div so we don't depend on bubbling
  // through whatever wrappers Library/Show layers around us. Skipped in
  // read-only mode (a student following a live read can't author selections).
  useEffect(() => {
    if (readOnly) return undefined;
    const el = contentRef.current;
    if (!el) return undefined;
    el.addEventListener('mouseup', handleSelectionMouseUp);
    return () => el.removeEventListener('mouseup', handleSelectionMouseUp);
  }, [handleSelectionMouseUp, readOnly]);

  // Keep the browser's blue selection visible while the toolbar is up.
  // Rebuild the offset map fresh against the live DOM — SelectionRenderer's
  // effect (a child, so it runs before this one) may have unwrapped and
  // normalized text nodes, invalidating any cached node references.
  useEffect(() => {
    if (!pending || !contentRef.current) return;
    const sel = window.getSelection();
    if (!sel) return;
    const map = buildTextOffsetMap(contentRef.current);
    offsetMapRef.current = map;
    const range = map.offsetsToRange(pending.start, pending.end);
    if (!range) return;
    try {
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (err) {
      console.warn('[ChapterReader] re-apply selection failed', err);
    }
  }, [pending]);

  // Dismiss on outside click / scroll / escape
  useEffect(() => {
    if (!pending) return;
    const onClick = (e) => {
      if (e.target.closest('[data-selection-toolbar]')) return;
      setPending(null);
      window.getSelection()?.removeAllRanges();
    };
    const onKey = (e) => { if (e.key === 'Escape') setPending(null); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [pending]);

  const endpoint = useMemo(() => (
    `/teacher/library/${book?.id}/chapters/${chapter?.id}/text_selections`
  ), [book?.id, chapter?.id]);

  async function persistSelection(payload) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
      body: JSON.stringify({ text_selection: payload }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      console.error('Selection save failed', body);
      return null;
    }
    const data = await res.json();
    return data.selection;
  }

  // Commit a new selections list: sync the ref (immediate), schedule the
  // React state update, and notify the parent OUTSIDE any updater so React
  // StrictMode's double-invocation can't cause stale/duplicated notifications.
  function commitSelections(next) {
    selectionsRef.current = next;
    setSelections(next);
    onSelectionsChange?.(next);
  }

  async function destroySelection(selection) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    const res = await fetch(`${endpoint}/${selection.id}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrf },
    });
    if (res.ok) {
      commitSelections(selectionsRef.current.filter((s) => s.id !== selection.id));
    }
  }

  async function updateSelection(id, payload) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    const res = await fetch(`${endpoint}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
      body: JSON.stringify({ text_selection: payload }),
    });
    if (res.ok) {
      const data = await res.json();
      commitSelections(selectionsRef.current.map((s) => (s.id === id ? data.selection : s)));
    }
  }

  // Hover lifecycle helpers. Share the same timer between the mark's
  // mouseleave and the menu's mouseleave so the menu stays open while the
  // cursor is moving over either.
  const HOVER_DISMISS_MS = 180;
  function scheduleHoverDismiss() {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setHoveredHighlight(null), HOVER_DISMISS_MS);
  }
  function cancelHoverDismiss() {
    clearTimeout(hoverTimeoutRef.current);
  }

  // Hover handler for ANY painted mark. We only open the hover menu for
  // highlights or analyzed_sentence marks (comments/close_readings live in
  // the sidebar). When a highlight overlaps an analyzed sentence, the menu
  // surfaces both the highlight actions (Delete / Add note) AND a "Go to
  // analysis" link — per the spec, clicking the blue underline used to
  // navigate but is now shadowed by the highlight mark, so the menu is the
  // only path into the analysis.
  function handleSelectionHover(sel, rect, isEnter) {
    cancelHoverDismiss();
    if (sel.kind !== 'highlight' && sel.kind !== 'analyzed_sentence') return;

    if (!isEnter) {
      scheduleHoverDismiss();
      return;
    }

    // Build the hover context: find an overlapping highlight AND/OR
    // analyzed sentence among the current renderable selections.
    const current = renderableSelections || [];
    let highlight = null;
    let analyzed = null;

    // Two texts "overlap" if either fully contains the other. We want the
    // hover menu to surface the cross-reference in BOTH directions:
    //   - highlight inside an analyzed sentence (highlight narrower)
    //   - highlight wider than the analyzed sentence
    const overlaps = (a, b) => !!(a && b && (a.includes(b) || b.includes(a)));

    if (sel.kind === 'highlight') {
      highlight = sel;
      const hText = (sel.selected_text || '').trim();
      analyzed = current.find(
        (s) => s.kind === 'analyzed_sentence' &&
               overlaps(hText, (s.selected_text || '').trim())
      ) || null;
    } else {
      analyzed = sel;
      const aText = (sel.selected_text || '').trim();
      highlight = current.find(
        (s) => s.kind === 'highlight' &&
               overlaps(aText, (s.selected_text || '').trim())
      ) || null;
    }

    setHoveredHighlight({ highlight, analyzed, rect });
  }

  function basePayload() {
    if (!pending) return null;
    return {
      text_start:    pending.start,
      text_end:      pending.end,
      selected_text: pending.text,
      selected_html: pending.html,
    };
  }

  function addAndNotify(sel) {
    commitSelections([...selectionsRef.current, sel]);
  }

  async function handleHighlight(color) {
    const base = basePayload();
    if (!base) return;
    const sel = await persistSelection({ ...base, kind: 'highlight', color });
    if (sel) addAndNotify(sel);
  }

  async function handleComment(note) {
    const base = basePayload();
    if (!base) return;
    const sel = await persistSelection({ ...base, kind: 'comment', note });
    if (sel) addAndNotify(sel);
  }

  async function handleCloseReading({ title, note }) {
    const base = basePayload();
    if (!base) return;
    const sel = await persistSelection({ ...base, kind: 'close_reading', title, note });
    if (sel) addAndNotify(sel);
  }

  function handleAnalyze() {
    if (!pending || !chapter) return;
    // Server will resolve the enclosing sentence from the offsets.
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/teacher/sentence_studio';
    form.style.display = 'none';
    appendField(form, 'authenticity_token', document.querySelector('meta[name="csrf-token"]')?.content);
    appendField(form, 'from_chapter_id', String(chapter.id));
    appendField(form, 'text_start', String(pending.start));
    appendField(form, 'text_end', String(pending.end));
    // Fallback text in case offsets somehow fail server-side.
    appendField(form, 'text', pending.text);
    document.body.appendChild(form);
    form.submit();
  }

  async function handleLookUp() {
    if (!pending?.expandedWord) return;
    try {
      const res = await fetch(`/teacher/dictionary/resolve?q=${encodeURIComponent(pending.expandedWord)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.id) {
          pushCrumb({
            level: 'word',
            label: data.lemma || pending.expandedWord,
            href: `/teacher/dictionary/${data.id}`,
          });
          router.visit(`/teacher/dictionary/${data.id}`);
          return;
        }
      }
      // Not found — nothing gentle to do yet; flash via alert.
      alert(`No dictionary entry for "${pending.expandedWord}"`);
    } catch (err) {
      console.error(err);
    }
  }

  const showListToast = useCallback((toast) => {
    setListToast(toast);
    if (toast) setTimeout(() => setListToast(null), 3500);
  }, []);

  async function handleAddToList(listId) {
    const word = pending?.expandedWord;
    if (!word) return;
    const list = (courseListOptions || []).find((l) => l.id === listId);
    if (!list) {
      showListToast({ kind: 'error', word, label: 'List not found' });
      return;
    }
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(
        `/teacher/courses/${list.course_id}/vocabulary/lists/${listId}/words`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
          body: JSON.stringify({
            surface_form: word,
            // `pending.text` is only what's under the cursor — for a
            // single-word add, the word itself. The offsets are what let the
            // server resolve the actual enclosing sentence out of the
            // chapter, and disambiguate a word that occurs more than once.
            context_sentence: pending.text,
            text_start: pending.start,
            text_end: pending.end,
            source_reference_type: chapter?.id ? 'BookChapter' : null,
            source_reference_id: chapter?.id || null,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showListToast({ kind: 'success', word, label: list.name });
      } else {
        showListToast({ kind: 'error', word, label: data.error || 'Could not add' });
      }
    } catch {
      showListToast({ kind: 'error', word, label: 'Network error' });
    }
  }

  // Merge real selections with synthetic underlines for sentences the user
  // has previously analyzed in Sentence Studio. The synthetic ones get IDs
  // prefixed with "analyzed-" so SelectionRenderer's <mark>-cleanup handles
  // them uniformly with real selections.
  const renderableSelections = useMemo(() => {
    const synth = (analyzedSentences || []).map((a) => ({
      id: `analyzed-${a.analysis_id}`,
      analysis_id: a.analysis_id,
      kind: 'analyzed_sentence',
      text_start: a.text_start,
      text_end: a.text_end,
      selected_text: a.text,
    }));
    return [...selections, ...synth];
  }, [selections, analyzedSentences]);

  function handleMarkClick(sel) {
    if (sel.kind === 'analyzed_sentence' && chapter?.id && sel.analysis_id) {
      router.visit(`/teacher/sentence_studio/${sel.analysis_id}?from_chapter=${chapter.id}`);
    }
  }

  return (
    <div className="relative">
      <div
        ref={contentRef}
        className={CHAPTER_CONTENT_CLASSES}
        style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}
        dangerouslySetInnerHTML={{ __html: html || '' }}
      />

      <SelectionRenderer
        containerRef={contentRef}
        offsetMap={offsetMap}
        selections={renderableSelections}
        onSelectionClick={handleMarkClick}
        onSelectionHover={handleSelectionHover}
      />

      {hoveredHighlight && !pending && (
        <HighlightHoverMenu
          rect={hoveredHighlight.rect}
          highlight={hoveredHighlight.highlight}
          analyzed={hoveredHighlight.analyzed}
          onDelete={(sel) => destroySelection(sel)}
          onConvert={(sel, note) => updateSelection(sel.id, { kind: 'comment', note })}
          onGoToAnalysis={(analyzed) => {
            if (chapter?.id && analyzed?.analysis_id) {
              router.visit(`/teacher/sentence_studio/${analyzed.analysis_id}?from_chapter=${chapter.id}`);
            }
          }}
          onDismiss={() => {
            cancelHoverDismiss();
            setHoveredHighlight(null);
          }}
          onCancelDismiss={cancelHoverDismiss}
          onScheduleDismiss={scheduleHoverDismiss}
        />
      )}

      {pending && (
        <SelectionToolbar
          rect={pending.rect}
          selection={{
            text: pending.text,
            wordCount: pending.wordCount,
            expandedWord: pending.expandedWord,
          }}
          onHighlight={handleHighlight}
          onComment={handleComment}
          onCloseReading={handleCloseReading}
          onAnalyze={handleAnalyze}
          onLookUp={handleLookUp}
          courseLists={courseListOptions}
          onAddToList={handleAddToList}
          onDismiss={() => {
            setPending(null);
            window.getSelection()?.removeAllRanges();
          }}
        />
      )}

      {listToast && (
        <div
          role="status"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] pointer-events-none"
          data-testid="add-to-list-toast"
        >
          <div
            className={cn(
              'px-4 py-2 rounded-full shadow-lg text-sm font-medium border',
              listToast.kind === 'success'
                ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-700'
                : 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-900/40 dark:text-rose-100 dark:border-rose-700'
            )}
          >
            {listToast.kind === 'success'
              ? `Added “${listToast.word}” to ${listToast.label}`
              : `Couldn’t add “${listToast.word}” — ${listToast.label}`}
          </div>
        </div>
      )}
    </div>
  );
}

function appendField(form, name, value) {
  if (value == null) return;
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = value;
  form.appendChild(input);
}

